/**
 * Budget chip — a ring and "$ left" beside a customer's name on every screen
 * that already shows it (threads/customer-billing-budget/mockup-access.html §1).
 * Customers without a live budget render nothing. Hover for the breakdown;
 * click opens the customer's Billing & budget tab.
 *
 * Each chip loads itself, but loads are batched per tick so a Closeout queue
 * of forty cards makes one request.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { chipSummary, longDate, money, pct, type BudgetHealth, type ChipBudget, type WorkCategory } from "./model";

let queued = new Map<string, Array<{ resolve: (v: ChipBudget[]) => void; reject: (e: unknown) => void }>>();
let timer: ReturnType<typeof setTimeout> | null = null;

async function flush() {
    const batch = queued;
    queued = new Map();
    timer = null;
    const ids = [...batch.keys()];
    for (let i = 0; i < ids.length; i += 200) {
        const chunk = ids.slice(i, i + 200);
        try {
            const r = await api.get<{ byCustomer: Record<string, ChipBudget[]> }>(
                `/api/budgets/by-customer?ids=${chunk.join(",")}`,
            );
            for (const id of chunk) for (const w of batch.get(id)!) w.resolve(r.byCustomer[id] ?? []);
        } catch (e) {
            for (const id of chunk) for (const w of batch.get(id)!) w.reject(e);
        }
    }
}

function loadChip(customerId: string): Promise<ChipBudget[]> {
    return new Promise((resolve, reject) => {
        queued.set(customerId, [...(queued.get(customerId) ?? []), { resolve, reject }]);
        timer ??= setTimeout(flush, 15);
    });
}

/**
 * finance.view — and false, not a crash, when a screen renders outside the app
 * shell (isolated component tests): the budget is an extra, never a dependency.
 */
export function useCanSeeMoney(): boolean {
    let auth: ReturnType<typeof useAuth> | null;
    try {
        // Safe: useAuth is exactly one useContext call whether or not it throws,
        // so hook order never changes. Tests mock @/lib/auth with only useAuth,
        // which rules out a separate non-throwing export.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        auth = useAuth();
    } catch {
        auth = null;
    }
    return typeof auth?.can === "function" && auth.can(PERMISSIONS.finance.view);
}

/** Live budgets for one customer; empty for anyone who can't see money. */
export function useCustomerBudgets(customerId: string | null | undefined): ChipBudget[] {
    const canSeeMoney = useCanSeeMoney();
    const enabled = !!customerId && canSeeMoney;
    const q = useQuery({
        queryKey: ["budget-chip", customerId],
        queryFn: () => loadChip(customerId!),
        enabled,
        staleTime: 60_000,
        retry: false,
    });
    return enabled ? (q.data ?? []) : [];
}

const TONE: Record<BudgetHealth, { pill: string; fill: string; track: string }> = {
    good: {
        pill: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300",
        fill: "#1c6b45",
        track: "#cfe6d8",
    },
    warn: {
        pill: "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
        fill: "#8a5a00",
        track: "#f1dfb3",
    },
    over: {
        pill: "border-red-300 bg-red-50 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300",
        fill: "#b42318",
        track: "#f5c9c4",
    },
};

export function BudgetRing({ usedPct, health, size = 16 }: { usedPct: number; health: BudgetHealth; size?: number }) {
    const t = TONE[health];
    const p = Math.max(0, Math.min(100, usedPct));
    return (
        <span
            aria-hidden
            className="relative inline-block shrink-0 rounded-full"
            style={{
                width: size,
                height: size,
                background: `conic-gradient(${t.fill} 0 ${p}%, ${t.track} ${p}% 100%)`,
                WebkitMask: `radial-gradient(circle, transparent ${size / 2 - 4}px, #000 ${size / 2 - 3.5}px)`,
                mask: `radial-gradient(circle, transparent ${size / 2 - 4}px, #000 ${size / 2 - 3.5}px)`,
            }}
        />
    );
}

const HEALTH_WORD: Record<BudgetHealth, string> = { good: "On track", warn: "Watch", over: "Over" };

export function BudgetChip({
    customerId,
    category,
    compact,
    className,
}: {
    customerId: string | null | undefined;
    /** Name only the budget this kind of work counts against (job, quote). */
    category?: WorkCategory;
    /** Shorter words for tight rows (Closeout cards). */
    compact?: boolean;
    className?: string;
}) {
    const budgets = useCustomerBudgets(customerId);
    const s = chipSummary(budgets, { category, compact });
    if (!customerId || !s) return null;
    const tone = TONE[s.health];
    return (
        <TooltipProvider delayDuration={150}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Link
                        to={`/customers/${customerId}?tab=budget`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="budget-chip"
                        className={cn(
                            "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border py-0.5 pl-1 pr-2.5 font-bold tabular-nums hover:brightness-95",
                            compact ? "text-[11px]" : "text-xs",
                            tone.pill,
                            className,
                        )}
                    >
                        <BudgetRing usedPct={s.usedPct} health={s.health} size={compact ? 13 : 16} />
                        <span className="truncate">{s.text}</span>
                    </Link>
                </TooltipTrigger>
                <TooltipContent
                    side="bottom"
                    align="start"
                    className="w-80 border bg-popover p-3 text-popover-foreground shadow-lg"
                >
                    <div className="space-y-2.5">
                        {s.budgets.map((b) => (
                            <div key={b.budgetId}>
                                <div className="flex items-baseline justify-between gap-2">
                                    <span className="truncate font-bold">{b.name}</span>
                                    <span className="shrink-0 text-[11px] text-muted-foreground">
                                        {HEALTH_WORD[b.health]}
                                    </span>
                                </div>
                                <div className="relative mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width: `${Math.min(100, b.usedPct)}%`,
                                            background: TONE[b.health].fill,
                                        }}
                                    />
                                    <div
                                        className="absolute inset-y-0 border-l border-dashed border-foreground/60"
                                        style={{ left: `${Math.min(100, b.calendarPct)}%` }}
                                    />
                                </div>
                                <div className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                                    {money(b.usedCents)} of {money(b.amountCents)} · {pct(b.usedPct)} used ·{" "}
                                    {pct(b.calendarPct)} of the way to {longDate(b.periodEnd)}
                                </div>
                                {b.health !== "good" && <div className="mt-0.5 text-[11px]">{b.reason}</div>}
                                {b.pendingCount > 0 && (
                                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                                        {money(b.pendingCents)} in {b.pendingCount} quote
                                        {b.pendingCount === 1 ? "" : "s"} waiting
                                    </div>
                                )}
                            </div>
                        ))}
                        <div className="border-t pt-2 text-[11px] font-bold text-primary">
                            Open Billing &amp; budget →
                        </div>
                    </div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
